"use client";

import * as yup from "yup";
import { ArraySchema } from "yup";
import Form from "@/src/components/common/form";
import capitalize from "@/src/utils/textTransform";
import { DefaultValues, FieldValues } from "react-hook-form";
import { forwardRef } from "react";

const createBaseSchema = (rules: SchemaConfig[string]): yup.AnySchema => {
  switch (rules.type) {
    case "boolean": {
      const schema = yup.boolean();
      if (rules.required) {
        return schema.oneOf(
          [true],
          rules.requiredMessage ||
            `${capitalize(rules.label || "")} must be checked`,
        );
      }
      return schema;
    }

    case "string":
      return yup.string();

    case "array": {
      let arraySchema = yup.array().of(yup.mixed());
      if (rules.validate && typeof rules.validate === "function") {
        arraySchema = applyCustomTest(
          arraySchema,
          rules.validate,
        ) as ArraySchema<any, yup.AnyObject, any, any>;
      }

      return arraySchema;
    }

    case "mixed":
    case "object":
      if (rules.fields) {
        let objectSchema = createNestedObjectSchema(rules.fields);
        if (rules.validate) {
          objectSchema = applyCustomTest(
            objectSchema,
            rules.validate,
          ) as yup.ObjectSchema<any, yup.AnyObject, any, any>;
        }
        return objectSchema;
      }

      return yup.mixed();

    default:
      throw new Error(`Unsupported schema type: ${rules.type}`);
  }
};

const createNestedObjectSchema = (
  fields: Record<string, SchemaConfig[string]>,
): yup.ObjectSchema<any> => {
  const nestedShape: Record<string, yup.AnySchema> = {};

  Object.entries(fields).forEach(([fieldName, fieldRules]) => {
    let fieldSchema = yup.string();
    if (fieldRules.required) {
      const requiredMessage =
        fieldRules.requiredMessage ||
        `${capitalize(fieldRules.label || fieldName)} is required`;
      fieldSchema = fieldSchema.required(requiredMessage);
    }
    nestedShape[fieldName] = fieldSchema;
  });

  return yup.object(nestedShape) as yup.ObjectSchema<any>;
};

const applyCustomTest = (
  schema: yup.AnySchema,
  validateFn: (
    value: any,
  ) =>
    | boolean
    | string
    | Record<string, string>
    | Array<Record<string, string> | undefined>,
): yup.AnySchema => {
  return schema.test({
    name: "custom-validation",
    test: function (value) {
      const result = validateFn(value);
      if (result === true) return true;

      // Handle array of errors (for arrays of objects)
      if (Array.isArray(result)) {
        // Find the first error and report it
        for (let idx = 0; idx < result.length; idx++) {
          const rowErr = result[idx];
          if (rowErr && typeof rowErr === "object") {
            const [field, message] = Object.entries(rowErr)[0];
            return this.createError({
              message,
              path: `${this.path}[${idx}].${field}`,
            });
          }
        }
        return true; // No errors found
      }

      // Handle object error (for flat objects)
      if (typeof result === "object") {
        const [errorKey, errorMessage] = Object.entries(result)[0];
        return this.createError({
          message: errorMessage,
          path: this.path.includes(".") ? `${this.path}.${errorKey}` : errorKey,
        });
      }

      // Handle string error (array-level)
      if (typeof result === "string") {
        return this.createError({ message: result });
      }

      return true;
    },
  });
};

const applyCommonValidations = (
  schema: yup.AnySchema,
  rules: SchemaConfig[string],
  fieldLabel: string,
  name: string,
): yup.AnySchema => {
  if (rules.required) {
    const requiredMessage =
      rules.requiredMessage || `${capitalize(fieldLabel)} is required`;
    if (rules.type === "string") {
      schema = (schema as yup.StringSchema).min(1, requiredMessage);
    } else if (rules.type === "array") {
      schema = (schema as ArraySchema<any, any>).min(1, requiredMessage);
    }
    schema = schema.required(requiredMessage);
  }

  if (rules.email) {
    schema = (schema as yup.StringSchema).email(
      `${capitalize(fieldLabel)} must be a valid email`,
    );
  }

  if (rules.when) {
    schema = schema.when(rules.when.field, {
      is: rules.when.is,
      then: (fieldSchema) =>
        rules.when?.then.required
          ? fieldSchema.required(`${capitalize(fieldLabel)} is required`)
          : fieldSchema,
      otherwise: (fieldSchema) => fieldSchema.notRequired(),
    });
  }

  if (rules.test) {
    if (typeof rules.test === "function") {
      schema = schema.test({
        name: `custom-${name}-validation`,
        test: function (value) {
          const result = rules.test!(value);

          return typeof result === "string"
            ? this.createError({ message: result })
            : result ||
                this.createError({
                  message: `Invalid ${capitalize(fieldLabel)}`,
                });
        },
      });
    } else if (typeof rules.test === "object") {
      const objectTest = rules.test as SchemaTest;
      schema = schema.test({
        name: objectTest.name || `custom-${name}-validation`,
        test: function (value) {
          const { isValid, params } = objectTest.validate(value);
          if (!isValid) {
            const message =
              typeof objectTest.message === "function"
                ? objectTest.message(params || {})
                : objectTest.message || `Invalid ${fieldLabel}`;

            return this.createError({ message, params: params || {} });
          }
          return true;
        },
      });
    }
  }

  return schema;
};

const buildSchema = (config: SchemaConfig): yup.ObjectSchema<any> => {
  const shape: Record<string, yup.AnySchema> = {};

  Object.entries(config).forEach(([name, rules]) => {
    const fieldLabel = rules.label || name;
    let schema = createBaseSchema(rules);

    schema = applyCommonValidations(schema, rules, fieldLabel, name);

    shape[name] = schema;
  });

  return yup.object(shape).required() as yup.ObjectSchema<any>;
};

const SchemaWrapper = forwardRef(
  <T extends FieldValues>(
    {
      config = {} as SchemaConfig,
      defaultValues = {} as DefaultValues<T>,
      children,
      hasDefaultAction = true,
      onSubmit,
    }: SchemaWrapperProps<T>,
    ref: React.Ref<FormRef>,
  ) => {
    const schema = buildSchema(config);

    return (
      <Form
        ref={ref}
        defaultValues={defaultValues}
        hasDefaultAction={hasDefaultAction}
        schema={schema}
        onSubmit={onSubmit ?? (() => {})}
      >
        {children}
      </Form>
    );
  },
);

SchemaWrapper.displayName = "SchemaWrapper";

export default SchemaWrapper;
